/* eslint-disable react/no-unused-prop-types */
import React, { useCallback, useMemo } from "react";
import { bool, func, shape } from "prop-types";
import { connect } from "react-redux";

import { TransferExecuteThirdParties } from "@ui-kit/components/index.es";

import { selectors as formSelectors } from "reducers/form";
import { selectors as frequentDestinationSelectors } from "reducers/frequentDestination";
import { selectors as i18nSelectors } from "reducers/i18n";
import { selectors as sessionSelectors } from "reducers/session";
import { selectors as settingsSelectors } from "reducers/settings";
import { selectors as templateSelectors } from "reducers/template";
import { selectors as transferSelectors } from "reducers/transfer";

import Message from "pages/v2/transfer/scheduler/Message";
import useTransferThirdPartiesExecuteLogic from "v3/pages/transfers/logic/useTransferThirdPartiesExecuteLogic";
import { createTransferExecuteFormLabels } from "v3/pages/transfers/transferExecuteLabels";
import {
    TransferExecuteModals,
    useTransferExecuteFormikSync,
    useTransferExecuteLimitsRenderer,
    resolveTransferExecuteCurrency,
} from "v3/pages/transfers/transferExecuteBridgeShared";

function TransferExecuteThirdPartiesBridge(props) {
    const logic = useTransferThirdPartiesExecuteLogic(props);
    const getLabels = useMemo(() => createTransferExecuteFormLabels("thirdParties"), []);
    const renderLimits = useTransferExecuteLimitsRenderer(logic);

    const renderScheduleSummary = useCallback(() => {
        if (logic.values?.scheduler?.selectedOption === undefined) {
            return null;
        }
        return <Message value={logic.values.scheduler || { valueDate: "" }} />;
    }, [logic.values?.scheduler]);

    const formikFields = useMemo(
        () => ({
            debitAccount: logic.values.debitAccount,
            transferKind: logic.values.transferKind,
            creditAccount: logic.values.creditAccount,
            amount: logic.values.amount,
            concept: logic.values.concept,
            reference: logic.values.reference,
            sendEmail: logic.values.sendEmail,
            email: logic.values.email,
            emailText: logic.values.emailText,
        }),
        [logic.values],
    );

    useTransferExecuteFormikSync(logic.formikProps, formikFields);

    const handleTadInfoClick = useCallback(() => {
        logic.openTADModal();
    }, [logic]);

    return (
        <>
            <TransferExecuteThirdParties
                getLabels={getLabels}
                loading={logic.isLoading}
                loadingSubmit={logic.fetchingSubmit}
                loadingDraft={logic.fetchingDraft}
                initialValues={logic.initialValues}
                validationSchema={logic.validationSchema}
                onSubmit={logic.onSubmit}
                setFormikProps={logic.setFormikProps}
                debitAccountOptions={logic.mapAccounts}
                transferKindOptions={logic.transferKindOptions}
                conceptOptions={logic.conceptsFiltered}
                onDebitAccountChange={logic.handleDebitAccountSelect}
                creditAccountConfirmed={logic.creditAccountConfirmed}
                creditAccountSearchCallback={logic.handleAccountSearch}
                onCreditAccountAccept={logic.handleConfirmCreditAccount}
                onCreditAccountCancel={logic.handleCreditAccountClear}
                onCreditAccountDelete={logic.handleCreditAccountClear}
                onCreditAccountChange={logic.handleCreditAccountInputChange}
                updateOnChange={logic.isTemplateOn}
                hideDebitAmount={logic.enableHideAmounts && logic.hideAmount && logic.isMobile}
                currency={resolveTransferExecuteCurrency(logic.selectedCurrency)}
                referenceMaxLength={logic.referenceMaxLength}
                extendedAmountEnabled={logic.extendedAmountVariableEnabled}
                showTadWarning={logic.tadWarningShowed}
                tadHelperText={logic.tadWarningText}
                showExtendedFields={logic.showFullList}
                renderLimits={renderLimits}
                showScheduleButton={logic.agendaVisible}
                onScheduleClick={logic.openScheduleModal}
                renderScheduleSummary={renderScheduleSummary}
                onSaveDraft={logic.onSaveDraft}
                onSaveTemplate={(values) => logic.handleCreateTemplate(values)}
                isDraftInactive={logic.draftDisabled}
                isTemplateInactive={logic.templateDisabled}
                continueExtraInactive={() => logic.continueDisabled}
                sendEmail={logic.values.sendEmail}
                onSendEmailToggle={logic.handleSendEmailToggle}
                onReferenceChange={logic.handleReferenceChange}
                onEmailChange={logic.handleEmailChange}
                onEmailTextChange={logic.handleEmailTextChange}
                onTransferKindChange={logic.handleTransferKindChange}
                onConceptChange={logic.handleConceptChange}
                onAmountBlur={logic.handleChangeAmount}
                onAmountKeyDown={logic.customHandleKeyDown}
                onTadInfoClick={handleTadInfoClick}
            />
            <TransferExecuteModals logic={logic} idActivity={logic.idActivity} idForm={logic.formId} showAgendaDrawer />
        </>
    );
}

TransferExecuteThirdPartiesBridge.propTypes = {
    dispatch: func.isRequired,
    isDesktop: bool.isRequired,
    onCreateTemplate: func.isRequired,
    agendaVisible: bool.isRequired,
    setAgendaVisible: func.isRequired,
    isTemplatesVisible: bool.isRequired,
    setIsTemplatesVisible: func.isRequired,
    location: shape({}).isRequired,
    isValidCbu: func.isRequired,
    creditAccountConfirmed: bool.isRequired,
    creditAccountDetail: shape({}),
    summary: shape({}).isRequired,
};

TransferExecuteThirdPartiesBridge.defaultProps = {
    creditAccountDetail: null,
};

const mapStateToProps = (state) => ({
    fetching: transferSelectors.getFetching(state),
    fetchingDraft: formSelectors.getFetchingDraft(state),
    fetchingSubmit: transferSelectors.getFetchingSubmit(state),
    accounts: transferSelectors.getAccounts(state),
    concepts: transferSelectors.getConcepts(state),
    limit: transferSelectors.getLimit(state),
    limitUsed: transferSelectors.getLimitUsed(state),
    limits: transferSelectors.getLimits(state),
    summary: transferSelectors.getSummary(state),
    templates: templateSelectors.getTemplateList(state),
    isCVU: transferSelectors.getIsCVU(state),
    creditAccountConfirmed: transferSelectors.getCreditAccountConfirmed(state),
    creditAccountDetail: transferSelectors.getCreditAccountDetails(state),
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    lang: i18nSelectors.getLang(state),
    isRetailEnvironment: sessionSelectors.isRetailEnvironment(state),
    hideAmount: settingsSelectors.getUserData(state)?.hideBalance,
    idEnvironment: frequentDestinationSelectors.getEnviromentId(state),
    listFrequentDestinations: frequentDestinationSelectors.getListFrequentDestinations(state),
    selectedFromAgenda: transferSelectors.getSelectedFromAgenda(state),
    frequentDestinationExistOnList: transferSelectors.getFrequentDestinationExistOnList(state),
    cvuCbuFromAlias: transferSelectors.getCvuCbuFromAlias(state),
});

export default connect(mapStateToProps)(TransferExecuteThirdPartiesBridge);
